import { useCallback } from 'react';

import {
  parseQuantityInputRaw,
  clampQuantity,
} from '@/shared/utils/quantityUtils';

import { SelectedOption } from '../types/ProductPageTypes';

export const useProductQuantityValidation = () => {
  const validateQuantity = useCallback(
    (
      selectedOption: SelectedOption,
      newQuantity: number,
    ): {
      isValid: boolean;
      validQuantity: number;
      message?: string;
    } => {
      const minQuantity = selectedOption.option.minimumOrderQuantity || 1;
      const maxQuantity = selectedOption.option.stock || 0;

      const validQuantity = clampQuantity(
        newQuantity,
        maxQuantity,
        minQuantity,
      );

      if (validQuantity !== newQuantity) {
        if (newQuantity < minQuantity) {
          return {
            isValid: false,
            validQuantity,
            message: `Minimum order quantity is ${minQuantity}`,
          };
        }

        if (newQuantity > maxQuantity) {
          return {
            isValid: false,
            validQuantity,
            message: `Maximum available quantity is ${maxQuantity}`,
          };
        }
      }

      return {
        isValid: true,
        validQuantity,
      };
    },
    [],
  );

  const parseQuantityInput = useCallback(
    (
      selectedOption: SelectedOption,
      inputValue: string,
    ): {
      isValid: boolean;
      validQuantity: number;
      message?: string;
    } => {
      const minQuantity = selectedOption.option.minimumOrderQuantity || 1;
      const originalValue = parseQuantityInputRaw(inputValue, minQuantity);
      const validationResult = validateQuantity(selectedOption, originalValue);

      return validationResult;
    },
    [validateQuantity],
  );

  return {
    validateQuantity,
    parseQuantityInput,
  };
};
